/**
 * Control Registry
 * Central mapping of control types to their rendering components.
 * Each entry carries both the runtime renderer AND the SchemaBuilder UI metadata
 * (icon, color, hasChilds, settingsConfig) so that SchemaBuilder.tsx derives
 * everything from this single source of truth.
 */

import React from 'react';
import { ControlComponentProps } from '../../../../types/ControlTypes';

/**
 * A single settings field shown in the SchemaBuilder for a control type.
 */
export interface SettingConfig {
  key: string;
  label: string;
  type: 'text' | 'number' | 'toggle' | 'options' | 'select-enum' | 'category-select' | 'json';
  placeholder?: string;
  options?: string[];
  /** Hide this field unless condition is met */
  showIf?: (settings: Record<string, any>) => boolean;
}

/**
 * Parameters passed to a control's createSchema function.
 */
export interface CreateSchemaParams {
  title?: string;
  description?: string;
  settings: Record<string, any>;
}

/**
 * Registry entry for a control type.
 * Fields prefixed with schema* are used exclusively by SchemaBuilder UI.
 */
export interface ControlRegistryEntry {
  // Runtime renderer
  component: React.ComponentType<ControlComponentProps>;

  // Shared
  label: string;
  description?: string;
  tier: 'core' | 'advanced' | 'specialized';

  // SchemaBuilder UI metadata
  icon?: React.ReactNode;
  color?: string;
  hasChilds?: boolean;
  settingsConfig?: SettingConfig[];

  // Schema generation
  defaultJsonType: string;
  createSchema: (params: CreateSchemaParams) => Record<string, any>;
}

/**
 * Control Registry - Maps control type string to its full entry.
 * Insertion order is preserved (JS object) — controls appear in
 * TypePickerPopover / dropdowns in registration order.
 */
export const CONTROL_REGISTRY: Record<string, ControlRegistryEntry> = {};

/**
 * Default schema creator: builds a JSON Schema property with x-control embedded.
 * Captures controlType and defaultJsonType via closure at registration time.
 */
function makeDefaultCreateSchema(
  controlType: string,
  defaultJsonType: string
): (params: CreateSchemaParams) => Record<string, any> {
  return (params: CreateSchemaParams) => {
    const schema: Record<string, any> = { type: defaultJsonType };
    if (params.title) schema.title = params.title;
    if (params.description) schema.description = params.description;
    const xControl: Record<string, any> = { type: controlType };
    Object.entries(params.settings).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        xControl[k] = v;
      }
    });
    schema['x-control'] = xControl;
    return schema;
  };
}

/**
 * Register a control component together with its SchemaBuilder UI metadata.
 * Call this once per control type during app initialisation.
 */
export function registerControl(
  controlType: string,
  component: React.ComponentType<ControlComponentProps>,
  label: string,
  config: {
    description?: string;
    tier?: 'core' | 'advanced' | 'specialized';
    icon?: React.ReactNode;
    color?: string;
    hasChilds?: boolean;
    settingsConfig?: SettingConfig[];
    defaultJsonType?: string;
    createSchema?: (params: CreateSchemaParams) => Record<string, any>;
  } = {}
): void {
  const jsonType = config.defaultJsonType ?? 'string';
  CONTROL_REGISTRY[controlType] = {
    component,
    label,
    description: config.description,
    tier: config.tier || 'core',
    icon: config.icon,
    color: config.color,
    hasChilds: config.hasChilds,
    settingsConfig: config.settingsConfig,
    defaultJsonType: jsonType,
    createSchema: config.createSchema ?? makeDefaultCreateSchema(controlType, jsonType),
  };
}

/**
 * Get a control component by type
 * @param controlType - The control type to look up
 * @returns The control component or null if not found
 */
export function getControlComponent(
  controlType: string
): React.ComponentType<ControlComponentProps> | null {
  const entry = CONTROL_REGISTRY[controlType];
  return entry?.component || null;
}

/**
 * Check if a control type is registered
 */
export function hasControl(controlType: string): boolean {
  return controlType in CONTROL_REGISTRY;
}

/**
 * Get all registered controls by tier
 */
export function getControlsByTier(tier: 'core' | 'advanced' | 'specialized'): ControlRegistryEntry[] {
  return Object.entries(CONTROL_REGISTRY)
    .filter(([_, entry]) => entry.tier === tier)
    .map(([_, entry]) => entry);
}

/**
 * Get all registered controls
 */
export function getAllControls(): Record<string, ControlRegistryEntry> {
  return { ...CONTROL_REGISTRY };
}
